import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useApiErrorHandler } from 'src/hooks/useApiErrorHandler';
import useUserLogged from 'src/hooks/useUserLogged';
import {
  isActiveBatchStatus,
  StockPhotoImportAccount,
  StockPhotoImportBatch,
  useCancelStockPhotoImportBatchMutation,
  useCreateAccountStockPhotoImportMutation,
  useLazyGetOpenStockPhotoImportBatchesQuery,
  useLazyGetStockPhotoImportBatchQuery,
} from 'src/redux/api/inventory/stockPhotoImports';

import { withPreservedAccount } from './helpers';

const POLL_INTERVAL_MS = 5000;

type StartAccountImportParams = {
  accountId: number;
  idempotencyKey: string;
  account?: StockPhotoImportAccount;
};

type StockPhotoImportContextValue = {
  batches: StockPhotoImportBatch[];
  isCreatingBatch: boolean;
  startAccountImport: (params: StartAccountImportParams) => Promise<boolean>;
  refreshOpenBatches: () => Promise<void>;
  cancelBatch: (batch: StockPhotoImportBatch) => Promise<void>;
  dismissBatch: (batch: StockPhotoImportBatch) => void;
};

const StockPhotoImportContext = createContext<StockPhotoImportContextValue | null>(null);

const areBatchesEqual = (left: StockPhotoImportBatch[], right: StockPhotoImportBatch[]) =>
  JSON.stringify(left) === JSON.stringify(right);

const isAccountScopedBatch = (batch: StockPhotoImportBatch) => batch.scope === 'account';

const withFallbackAccount = (
  batch: StockPhotoImportBatch,
  fallback?: StockPhotoImportAccount
): StockPhotoImportBatch => (batch.account || !fallback ? batch : { ...batch, account: fallback });

export const StockPhotoImportProvider = ({ children }: { children: React.ReactNode }) => {
  const handleError = useApiErrorHandler();
  const handleErrorRef = useRef(handleError);
  const { isSuperAdminOnlyLogged } = useUserLogged();
  const isSuperAdminOnlyLoggedRef = useRef(isSuperAdminOnlyLogged);
  const [batches, setBatches] = useState<StockPhotoImportBatch[]>([]);
  const batchesRef = useRef(batches);
  const [fetchOpenBatches] = useLazyGetOpenStockPhotoImportBatchesQuery();
  const [fetchBatch] = useLazyGetStockPhotoImportBatchQuery();
  const [createBatch, { isLoading: isCreatingBatch }] = useCreateAccountStockPhotoImportMutation();
  const [cancelBatchMutation] = useCancelStockPhotoImportBatchMutation();

  handleErrorRef.current = handleError;
  batchesRef.current = batches;
  isSuperAdminOnlyLoggedRef.current = isSuperAdminOnlyLogged;

  const mergeBatch = useCallback((incoming: StockPhotoImportBatch) => {
    if (!isSuperAdminOnlyLoggedRef.current || !isAccountScopedBatch(incoming)) {
      return;
    }

    setBatches((current) => {
      const existing = current.find((item) => item.id === incoming.id);
      const batch = withPreservedAccount(incoming, existing);

      if (!existing) {
        return [...current, batch];
      }

      return current.map((item) => (item.id === batch.id ? batch : item));
    });
  }, []);

  const refreshOpenBatches = useCallback(async () => {
    if (!isSuperAdminOnlyLoggedRef.current) {
      return;
    }

    try {
      const openBatches = (await fetchOpenBatches(undefined, false).unwrap()).filter(
        isAccountScopedBatch
      );

      if (!isSuperAdminOnlyLoggedRef.current) {
        return;
      }

      setBatches((current) => {
        const incomingById = new Map(
          openBatches.map((incoming) => {
            const existing = current.find((item) => item.id === incoming.id);
            return [incoming.id, withPreservedAccount(incoming, existing)] as const;
          })
        );

        const next = openBatches.map((incoming) => incomingById.get(incoming.id)!);

        current.forEach((existing) => {
          if (!incomingById.has(existing.id)) {
            next.push(existing);
          }
        });

        return areBatchesEqual(current, next) ? current : next;
      });
    } catch (error) {
      handleErrorRef.current(error);
    }
  }, [fetchOpenBatches]);

  const pollActiveBatches = useCallback(async () => {
    if (!isSuperAdminOnlyLoggedRef.current) {
      return;
    }

    const activeBatches = batchesRef.current.filter((batch) => isActiveBatchStatus(batch.status));

    await Promise.all(
      activeBatches.map(async (batch) => {
        try {
          const updated = await fetchBatch(batch.id, false).unwrap();
          mergeBatch(updated);
        } catch (error) {
          handleErrorRef.current(error);
        }
      })
    );
  }, [fetchBatch, mergeBatch]);

  const activeBatchIds = batches
    .filter((batch) => isActiveBatchStatus(batch.status))
    .map((batch) => batch.id)
    .join(',');

  useEffect(() => {
    if (!isSuperAdminOnlyLogged) {
      setBatches([]);
    }
  }, [isSuperAdminOnlyLogged]);

  useEffect(() => {
    if (!isSuperAdminOnlyLogged || !activeBatchIds) {
      return undefined;
    }

    const intervalId = window.setInterval(() => {
      void pollActiveBatches();
    }, POLL_INTERVAL_MS);

    return () => window.clearInterval(intervalId);
  }, [isSuperAdminOnlyLogged, activeBatchIds, pollActiveBatches]);

  const startAccountImport = useCallback(
    async ({ accountId, idempotencyKey, account }: StartAccountImportParams) => {
      try {
        const batch = await createBatch({ accountId, idempotencyKey }).unwrap();
        mergeBatch(withFallbackAccount(batch, account));
        return true;
      } catch (error) {
        handleErrorRef.current(error);
        return false;
      }
    },
    [createBatch, mergeBatch]
  );

  const cancelBatch = useCallback(
    async (batch: StockPhotoImportBatch) => {
      try {
        const canceled = await cancelBatchMutation(batch.id).unwrap();
        mergeBatch(canceled);
      } catch (error) {
        handleErrorRef.current(error);
      }
    },
    [cancelBatchMutation, mergeBatch]
  );

  const dismissBatch = useCallback((batch: StockPhotoImportBatch) => {
    setBatches((current) => current.filter((item) => item.id !== batch.id));
  }, []);

  const value = useMemo(
    () => ({
      batches,
      isCreatingBatch,
      startAccountImport,
      refreshOpenBatches,
      cancelBatch,
      dismissBatch,
    }),
    [batches, isCreatingBatch, startAccountImport, refreshOpenBatches, cancelBatch, dismissBatch]
  );

  return (
    <StockPhotoImportContext.Provider value={value}>{children}</StockPhotoImportContext.Provider>
  );
};

export const useStockPhotoImport = () => {
  const context = useContext(StockPhotoImportContext);

  if (!context) {
    throw new Error('useStockPhotoImport must be used within StockPhotoImportProvider');
  }

  return context;
};

export default StockPhotoImportContext;
